import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {QueryRef} from 'apollo-angular';
import {Subscription} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import {UserService} from '../../service/user.service';
import {FilterService} from '../../service/filter.service';
import {
    Country,
    GetCountriesGQL,
    GetCountriesQuery,
    GetUnitiesByCountryGQL,
    GetUnitiesByCountryQuery,
    Unity,
    User
} from '../../../generated/graphql';
import {
    MOCK_AVG_DWELL_BY_BLOC,
    MOCK_BLOC_OCCUPANCY,
    MOCK_GATE_PEAKS,
    MOCK_KPIS,
    MOCK_OPERATION_QUALITY,
    MOCK_WEEKLY_ADHESION
} from './dashboard-carpool.mock';

declare var moment;
declare var $;

interface KpiCard {
    label: string;
    value: string;
    hint: string;
    variant: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
    selector: 'app-dashboard-carpool',
    templateUrl: './dashboard-carpool.component.html',
    styleUrls: ['./dashboard-carpool.component.scss']
})
export class DashboardCarpoolComponent extends BaseComponent implements OnInit, OnDestroy {

    filterKey = 'dashboard_carpool_filter';
    user: User = new User();

    subscription: Subscription;

    countries: Array<Country>;
    countryQuery: QueryRef<GetCountriesQuery>;

    unities: Array<Unity>;
    unityQuery: QueryRef<GetUnitiesByCountryQuery>;

    selectedCountryId: string;
    filter: any = {};

    kpiCards: KpiCard[] = [];

    myData1: any[] = [['', 0, 0]];   // ocupação por bloco
    myData2: any[] = [['', 0]];      // adesão semanal
    myData3: any[] = [['', 0, 0]];   // picos de portaria
    myData4: any[] = [['', 0, 0]];   // qualidade da operação
    myData5: any[] = [['', 0]];      // tempo médio por bloco

    chart1Names = ['Bloco', 'Ocupado', 'Disponível'];
    chart1NamesES = ['Bloque', 'Ocupado', 'Disponible'];

    chart2Names = ['Semana', 'Entradas'];
    chart2NamesES = ['Semana', 'Entradas'];

    chart3Names = ['Horário', 'Entradas', 'Saídas'];
    chart3NamesES = ['Horario', 'Entradas', 'Salidas'];

    chart4Names = ['Mês', 'Registros normais', 'Correções manuais'];
    chart4NamesES = ['Mes', 'Registros normales', 'Correcciones manuales'];

    chart5Names = ['Bloco', 'Horas'];
    chart5NamesES = ['Bloque', 'Horas'];

    columnNames1: string[] = [];
    columnNames2: string[] = [];
    columnNames3: string[] = [];
    columnNames4: string[] = [];
    columnNames5: string[] = [];

    // Ocupação por bloco: ABSOLUTO (mostra capacidade real). Ocupado escuro, disponível cinza claro.
    options1 = {isStacked: true, legend: {position: 'top'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#032E58', '#D5D5D5']};
    options2 = {legend: {position: 'none'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#032E58']};
    options3 = {legend: {position: 'top'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#032E58', '#FAE100']};
    options4 = {legend: {position: 'top'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#004B93', '#C0392B']};
    options5 = {legend: {position: 'none'}, hAxis: {minValue: 0}, bar: {groupWidth: '45%'}, colors: ['#004B93']};

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private getCountriesGQL: GetCountriesGQL,
                private getUnitiesByCountryGQL: GetUnitiesByCountryGQL,
                private filterService: FilterService) {
        super(router, translate);
        this.subscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            $(() => {
                this.checkSubtitle();
                this.buildKpis();
                this.buildCharts();
            });
        });
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
            }, error: err => super.onError(err)
        });

        this.getCountries();
        this.getUnitiesInit();

        const savedFilter = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = savedFilter;
            this.selectedCountryId = savedFilter.countryId;
            if (savedFilter.countryId) {
                this.getUnities();
            }
        } else {
            this.setDefaultPeriod();
        }

        this.initDatePicker();

        this.checkSubtitle();
        this.buildKpis();
        this.buildCharts();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setDefaultPeriod() {
        this.filter.start = moment().startOf('day').subtract(30, 'days').utcOffset(0, true).format();
        this.filter.end = moment().endOf('day').utcOffset(0, true).format();
    }

    initDatePicker() {
        this.setDatePickerRange('dashboard-daterangepicker', (start, end, label) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        }, this.filter.start, this.filter.end);
    }

    getCountries() {
        this.countryQuery = this.getCountriesGQL.watch();
        this.countryQuery.valueChanges.subscribe(({data}) => {
            this.countries = data.countries as Array<Country>;
        });
    }

    getUnitiesInit() {
        this.unityQuery = this.getUnitiesByCountryGQL.watch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
        this.unityQuery.valueChanges.subscribe(({data}) => {
            this.unities = data.getUnitiesByCountry as Array<Unity>;
        });
    }

    getUnities() {
        this.unityQuery.refetch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
    }

    applyFilter() {
        if (!this.isNullOrUndefined(this.selectedCountryId)) {
            this.filter.countryId = this.selectedCountryId;
        } else {
            this.filter.countryId = undefined;
        }
        this.filterService.setFilter(this.filterKey, this.filter);

        super.toggleLoader(true);
        this.buildKpis();
        this.buildCharts();
        super.toggleLoader(false);
        // TODO(dados-reais): aplicar filtro
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {};
        this.selectedCountryId = null;
        this.setDefaultPeriod();
    }

    // ---------------------------------------------------------------------
    buildKpis() {
        const k = MOCK_KPIS;
        const t = (key: string) => this.translate.instant('dashboards.carpool.' + key);
        this.kpiCards = [
            {label: t('kpi.rides'), value: this.fmtInt(k.ridesInPeriod), hint: '', variant: 'default'},
            {
                label: t('kpi.currentOccupancy'),
                value: this.fmtInt(k.currentOccupancy),
                hint: `${t('kpi.of')} ${k.totalCapacity} ${t('kpi.slots')}`,
                variant: 'default'
            },
            {label: t('kpi.occupancyRate'), value: this.fmtPercent(k.occupancyRate), hint: '', variant: 'default'},
            {label: t('kpi.uniqueVehicles'), value: this.fmtInt(k.uniqueVehicles), hint: '', variant: 'default'},
            {label: t('kpi.manualRate'), value: this.fmtPercent(k.manualRecordsPercent), hint: '', variant: 'warning'},
            {label: t('kpi.carsSaved'), value: this.fmtInt(k.carsSavedEstimate), hint: t('kpi.estimate'), variant: 'success'},
        ];
    }

    buildCharts() {
        this.myData1 = MOCK_BLOC_OCCUPANCY.map(r => [r.bloc, r.occupied, r.available]);
        this.myData2 = MOCK_WEEKLY_ADHESION.map(r => [r.week, r.entries]);
        this.myData3 = MOCK_GATE_PEAKS.map(r => [r.hour, r.entries, r.exits]);
        this.myData4 = MOCK_OPERATION_QUALITY.map(r => [r.month, r.normal, r.manual]);
        this.myData5 = MOCK_AVG_DWELL_BY_BLOC.map(r => [r.bloc, r.hours]);
    }

    checkSubtitle() {
        if (this.translate.currentLang === 'pt') {
            this.columnNames1 = this.chart1Names;
            this.columnNames2 = this.chart2Names;
            this.columnNames3 = this.chart3Names;
            this.columnNames4 = this.chart4Names;
            this.columnNames5 = this.chart5Names;
        } else {
            this.columnNames1 = this.chart1NamesES;
            this.columnNames2 = this.chart2NamesES;
            this.columnNames3 = this.chart3NamesES;
            this.columnNames4 = this.chart4NamesES;
            this.columnNames5 = this.chart5NamesES;
        }
    }

    fmtInt(n: number): string {
        return n.toString();
    }

    fmtPercent(n: number): string {
        return n.toFixed(1).replace('.', ',') + '%';
    }
}
